import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LinkTrackingService } from '@api'
import { useUnit } from 'effector-react'

import { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import { $creatorId } from '@/models/creators.ts'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback'

interface Props {
  excludeValues?: string[]
}

export function useTrackingLinks({ excludeValues = [] }: Props = {}) {
  const creatorId = useUnit($creatorId)
  const [links, setLinks] = useState<SelectOptionsType>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const hasMoreRef = useRef(true)

  const loadMoreLinks = useCallback(
    async (page: number, search: string) => {
      if (!creatorId || isLoading || !hasMoreRef.current) return

      try {
        setIsLoading(true)
        const response = await LinkTrackingService.paginatedLinks({
          creatorId,
          type: 'Tracking',
          page: page,
          searchQuery: search,
          pagination: {
            size: 20,
            page: page,
          },
        })
        const newLinks: SelectOptionsType = response.content
          ? response.content
              .map((el) => ({
                value: el.id ? String(el.id) : '',
                label: el.name ? String(el.name) : '',
              }))
              .filter((el) => el.value && el.label)
          : []

        setLinks(page === 0 ? newLinks : [...links, ...newLinks])
        hasMoreRef.current = !response.last
        setHasMore(!response.last)
      } catch (error) {
        console.error('Error loading tracking links:', error)
      } finally {
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatorId, isLoading, links, excludeValues],
  )

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  useEffect(
    () => {
      loadMoreLinks(0, '')
      //   Run loadMoreLinks only once on creatorId update
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatorId],
  )

  const fetchLinksDebounced = useDebouncedCallback(500, loadMoreLinks)

  const searchTrackingLinks = useCallback(
    async (query: string) => {
      if (!creatorId) return

      setSearchQuery(query)
      setHasMore(true)
      setCurrentPage(0)
      await fetchLinksDebounced(0, query)
    },
    [creatorId, fetchLinksDebounced],
  )

  const loadMoreTrackingLinks = useCallback(async () => {
    if (!creatorId) return

    const newPage = currentPage + 1

    setCurrentPage(newPage)
    await loadMoreLinks(newPage, searchQuery)
  }, [creatorId, currentPage, loadMoreLinks, searchQuery])

  const processedLinks = useMemo(
    () =>
      links.map((link) => ({
        ...link,
        disabled: excludeValues.includes(link.value),
      })),
    [links, excludeValues],
  )

  return {
    links: processedLinks,
    searchQuery,
    isLoading,
    hasMore,
    searchTrackingLinks,
    loadMoreTrackingLinks,
  }
}
